/**
 * Email.tsx · 09_APP · OS-006 · C12b, C32, C41 · LIVE on her real mail since v79 (2026-10-26, D-0100)
 *
 * Her words, 2026-10-26: "let's go ahead and build that out, and then let's see, let's get some emails going out."
 * Same layout she approved Oct 19 ("I do like how you have the Emily and then info. I love that."):
 * views on the left, the list in the middle, the conversation and Claude's draft on the right.
 *
 * Her ask the same morning (D-0102): "every one sent with AI will have a little astrick or something in red that
 * alerts me that you sent it." Any message this system sent whose words Claude wrote carries a red ✱.
 *
 * Send = approve this exact version and queue it (os_draft_send). The send step (mail-send) sends from her Gmail
 * within a minute, with "With gratitude," and her signature. If she changed the draft, her words are saved first as a
 * new version (written_by emily), and that version is the one approved.
 * Not now = the draft is set aside and the email leaves Needs you. Nothing is deleted.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { os, type MailRow, type MailThread } from "../lib/os";

type Box = "emily" | "info";
type View = "needs" | "handled" | "waiting" | "all" | "sent" | "junk" | "personal";

const VIEWS: Array<[View, string]> = [
  ["needs", "Needs you"], ["handled", "Claude handled today"], ["waiting", "Waiting on them"],
  ["all", "All mail"], ["sent", "Sent"], ["junk", "Junk to review"], ["personal", "Personal, yours only"],
];

const EMPTY: Record<View, string> = {
  needs: "Nothing needs you right now.",
  handled: "Nothing sent by your assistant yet today.",
  waiting: "Nobody is waiting on a reply from you.",
  all: "No mail yet.",
  sent: "Nothing sent yet.",
  junk: "No junk to review.",
  personal: "Nothing here.",
};

const when = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date().toLocaleDateString("en-US", { timeZone: "America/Los_Angeles" });
  const day = d.toLocaleDateString("en-US", { timeZone: "America/Los_Angeles" });
  return day === today
    ? d.toLocaleTimeString("en-US", { timeZone: "America/Los_Angeles", hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric" });
};

const clean = (s: string | null) => (s ?? "").replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

/** The red mark she asked for. */
function AiMark() {
  return <span className="mail-ai" title="Sent by your AI assistant" aria-label="Sent by your AI assistant">✱</span>;
}

export function Email() {
  const [box, setBox] = useState<Box>("emily");
  const [view, setView] = useState<View>("needs");
  const [rows, setRows] = useState<MailRow[] | null>(null);
  const [counts, setCounts] = useState<{ needs: number; handled: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [thread, setThread] = useState<MailThread | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const paneRef = useRef<HTMLElement | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [r, c] = await Promise.all([os.mailView(view, box), os.mailCounts(box)]);
      setRows(r);
      setCounts(c);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [view, box]);

  useEffect(() => { setRows(null); setOpenId(null); setThread(null); void load(); }, [load]);

  async function openRow(id: string) {
    setOpenId(id);
    setThread(null);
    setMsg(null);
    try {
      const t = await os.mailThread(id);
      setThread(t);
      setBody(t.draft?.body ?? "");
      setTimeout(() => paneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    }
  }

  async function send() {
    if (!thread?.draft) return;
    setBusy(true);
    setMsg(null);
    try {
      let versionId = thread.draft.version_id;
      if (body.trim() !== thread.draft.body.trim()) {
        versionId = (await os.draftEdit(thread.draft.draft_id, thread.draft.subject, body)).version_id;
      }
      await os.draftSend(versionId);
      setMsg({ ok: true, text: `Sending to ${thread.draft.to.join(", ")}. It goes out from your Gmail within a minute, with your signature.` });
      void load();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function notNow() {
    if (!thread) return;
    setBusy(true);
    try {
      await os.draftSkip(thread.id);
      setMsg({ ok: true, text: "Set aside. It left Needs you, and nothing was deleted." });
      void load();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const draftOpen = thread?.draft && !["sent", "queued", "discarded"].includes(thread.draft.status);

  return (
    <section className="mailtab" aria-label="Email">
      <div className="mail-grid">
        <nav className="mail-col mail-views" aria-label="Mailboxes and views">
          <div className="mail-boxes" role="tablist">
            <button role="tab" aria-selected={box === "emily"} className={box === "emily" ? "on" : ""} onClick={() => setBox("emily")}>Emily</button>
            <button role="tab" aria-selected={box === "info"} className={box === "info" ? "on" : ""} onClick={() => setBox("info")}>info@</button>
          </div>
          {VIEWS.map(([k, label]) => (
            <button key={k} className={view === k ? "mail-view on" : "mail-view"} onClick={() => setView(k)}>
              <span>{label}</span>
              {k === "needs" && counts && counts.needs > 0 && <span className="mail-n">{counts.needs}</span>}
              {k === "handled" && counts && counts.handled > 0 && <span className="mail-n teal">{counts.handled}</span>}
            </button>
          ))}
        </nav>

        <div className="mail-col mail-list">
          <div className="mail-sec">
            {VIEWS.find(([k]) => k === view)?.[1]}
            {view === "sent" && <span className="mail-legend"> · <AiMark /> = sent by your AI assistant</span>}
          </div>
          {err && <div className="refusebox">{err}</div>}
          {!rows && !err && <div className="mail-empty">Loading your mail…</div>}
          {rows && rows.length === 0 && <div className="mail-empty">{EMPTY[view]}</div>}
          {rows?.map((r) => (
            <button key={r.id} className={`mail-row${r.id === openId ? " on" : ""}${r.unread ? " unread" : ""}`} onClick={() => void openRow(r.id)}>
              <span className="mail-who">
                <b>{r.ai && <AiMark />}{view === "sent" ? `To ${r.to ?? ""}` : clean(r.person ?? r.from)}</b>
                <span className="mail-when">{when(r.at)}</span>
              </span>
              <span className="mail-subj">{clean(r.subject) || "(no subject)"}</span>
              {view === "needs"
                ? <span className="mail-why">{r.has_draft ? "Draft ready" : "Needs your answer"}</span>
                : <span className="mail-why">{clean(r.snippet).slice(0, 110)}</span>}
            </button>
          ))}
        </div>

        {openId && (
          <article className="mail-col mail-pane" ref={paneRef}>
            {!thread ? <div className="mail-empty">Opening…</div> : (
              <>
                <h2>{clean(thread.subject) || "(no subject)"}</h2>
                <div className="mail-why">{thread.person ? `${thread.person} · ` : ""}{thread.messages.length} message{thread.messages.length === 1 ? "" : "s"}</div>
                {thread.note && <div className="mail-know"><b>Claude's note:</b> {thread.note}</div>}
                {thread.messages.slice(-4).map((m) => (
                  <div key={m.id} className={m.direction === "out" ? "mail-msg out" : "mail-msg"}>
                    <div className="mail-msghead">
                      <b>{m.ai && <AiMark />}{m.direction === "out" ? "You" : clean(m.from)}</b>
                      <span className="mail-when">{when(m.at)}</span>
                    </div>
                    <div className="mail-body">{clean(m.body).split(/\nOn .{10,120}wrote:\n/)[0]!.slice(0, 2500)}</div>
                  </div>
                ))}
                {thread.messages.length > 4 && <div className="mail-why">Showing the last 4 of {thread.messages.length}.</div>}

                {draftOpen && thread.draft && (
                  <>
                    <label className="mail-draftlbl" htmlFor="mail-draft-body">
                      {thread.draft.written_by === "claude" ? "Claude's draft, in your voice" : "Your draft"} · to {thread.draft.to.join(", ")}
                      {thread.draft.from === "info@connect2change.org" ? " · from info@" : ""}
                    </label>
                    <textarea id="mail-draft-body" className="mail-draft" value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
                    <div className="mail-why">Change anything you like. "With gratitude," and your signature are added when it sends.</div>
                    <div className="mail-actions">
                      <button className="btn go" disabled={busy || !body.trim()} onClick={() => void send()}>{busy ? "Working…" : "Send"}</button>
                      <button className="btn plain" disabled={busy} onClick={() => void notNow()}>Not now</button>
                    </div>
                  </>
                )}
                {thread.draft && !draftOpen && <div className="mail-why">Draft status: {thread.draft.status}.</div>}
                {msg && <div className={msg.ok ? "mail-ok" : "refusebox"} role="status">{msg.text}</div>}
              </>
            )}
          </article>
        )}
      </div>
    </section>
  );
}
